import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { BarChart3, MessageCircle, ChevronRight, Loader2, Plus, Send, Image as ImageIcon, ArrowLeft, Flag, X, Users, Search, MoreVertical, ShieldOff, Star, Phone, Trash2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { surveyAPI, chatAPI, recommendationAPI } from '../../services/api';

// --- Tiles ---
export function SurveysTile({ count, onClick }) {
  if (count === 0) return null;
  return (
    <Card data-testid="surveys-tile" className="shadow border-0 cursor-pointer hover:shadow-lg" onClick={onClick}>
      <CardContent className="p-3 sm:p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-xl bg-indigo-100 flex items-center justify-center"><BarChart3 className="w-4 h-4 sm:w-6 sm:h-6 text-indigo-600" /></div>
            <div>
              <p className="text-sm sm:text-base font-medium text-slate-600 leading-tight">Polls & Surveys</p>
              <p className="text-3xl sm:text-4xl font-extrabold text-indigo-600">{count}</p>
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-slate-300 hidden sm:block" />
        </div>
      </CardContent>
    </Card>
  );
}

export function ChatTile({ unreadCount, recent, onClick }) {
  return (
    <Card data-testid="chat-tile" className="shadow border-0 cursor-pointer hover:shadow-lg" onClick={onClick}>
      <CardContent className="p-3 sm:p-5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <div className="w-9 h-9 sm:w-12 sm:h-12 rounded-xl bg-cyan-100 flex items-center justify-center relative shrink-0">
              <MessageCircle className="w-4 h-4 sm:w-6 sm:h-6 text-cyan-600" />
              {unreadCount > 0 && <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] rounded-full px-1.5 py-0.5 font-bold">{unreadCount}</span>}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm sm:text-base font-medium text-slate-600 leading-tight">Neighbors Chat</p>
              <p className="text-3xl sm:text-4xl font-extrabold text-cyan-600">{unreadCount > 0 ? unreadCount : '—'}</p>
              {recent && <p data-testid="chat-tile-recent" className="text-xs sm:text-xs text-cyan-700/80 truncate mt-0.5">{recent}</p>}
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-slate-300 hidden sm:block shrink-0" />
        </div>
      </CardContent>
    </Card>
  );
}

// --- Surveys Modal ---
export function SurveysModal({ open, onOpenChange }) {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null); // { aggregated, my_answers, response_count, expires_at, anonymous }
  const [resultsLoading, setResultsLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await surveyAPI.listForResident(); setSurveys(data || []); }
    catch { toast.error('Failed to load surveys'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  // When picking an already-responded survey, fetch aggregated results
  useEffect(() => {
    if (!active || !active.responded) { setResults(null); return; }
    setResultsLoading(true);
    surveyAPI.aggregated(active.id)
      .then(r => setResults(r.data))
      .catch(() => toast.error('Failed to load results'))
      .finally(() => setResultsLoading(false));
  }, [active]);

  const submit = async () => {
    if (!active) return;
    const ans = active.questions.map((_, idx) => answers[idx]);
    if (ans.some(a => a === undefined)) return toast.error('Please answer every question');
    if (!window.confirm('Once submitted, your response CANNOT be changed. Continue?')) return;
    setSubmitting(true);
    try {
      await surveyAPI.respond(active.id, ans);
      toast.success('Response submitted!');
      // Stay on the survey to view results
      const refreshed = await surveyAPI.listForResident();
      setSurveys(refreshed.data || []);
      const updated = (refreshed.data || []).find(s => s.id === active.id);
      if (updated) setActive(updated);
      setAnswers({});
    }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to submit'); }
    finally { setSubmitting(false); }
  };

  const formatExpiry = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }); }
    catch { return iso.slice(0, 16); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setActive(null); setAnswers({}); setResults(null); } onOpenChange(v); }}>
      <DialogContent data-testid="surveys-modal" className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          {active ? (
            <><DialogTitle><button onClick={() => { setActive(null); setResults(null); }} className="text-sm text-slate-500 mr-2 inline-flex items-center"><ArrowLeft className="w-4 h-4 mr-1" />Back</button>{active.title}</DialogTitle><DialogDescription>{active.description || (active.responded ? 'Live results' : 'Tap an option for each question')}</DialogDescription></>
          ) : (
            <><DialogTitle>Polls & Surveys</DialogTitle><DialogDescription>Make your voice heard</DialogDescription></>
          )}
        </DialogHeader>
        {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto py-6 text-indigo-500" /> : active ? (
          active.responded ? (
            // --- Results view ---
            <div className="space-y-4 py-2">
              <div data-testid="survey-locked-banner" className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-800">
                <p className="font-semibold">✓ You voted on this {active.type}.</p>
                <p className="mt-0.5">Your response cannot be changed. Live results shown below — visible until <span className="font-semibold">{formatExpiry(active.expires_at)}</span>.</p>
              </div>
              {resultsLoading || !results ? <Loader2 className="w-5 h-5 animate-spin mx-auto py-4 text-indigo-500" /> : (
                <div className="space-y-4">
                  <p className="text-xs text-slate-500">{results.response_count} response{results.response_count !== 1 ? 's' : ''} so far</p>
                  {results.aggregated.map((q, qIdx) => {
                    const total = q.counts.reduce((a, b) => a + b, 0) || 1;
                    const myChoice = results.my_answers ? results.my_answers[qIdx] : null;
                    return (
                      <div key={qIdx} className="space-y-2" data-testid={`survey-result-q-${qIdx}`}>
                        <p className="text-sm font-semibold">{qIdx + 1}. {q.question}</p>
                        {q.options.map((o, oIdx) => {
                          const c = q.counts[oIdx] || 0;
                          const pct = ((c / total) * 100).toFixed(0);
                          const mine = myChoice === oIdx;
                          return (
                            <div key={oIdx} className={`p-2 rounded-lg border ${mine ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 bg-slate-50'}`}>
                              <div className="flex justify-between text-xs mb-1">
                                <span className={mine ? 'font-semibold text-indigo-800' : 'text-slate-700'}>{o}{mine && ' · your vote'}</span>
                                <span className="font-mono text-slate-600">{c} ({pct}%)</span>
                              </div>
                              <div className="w-full bg-white rounded-full h-2 border border-slate-200"><div className={`${mine ? 'bg-indigo-500' : 'bg-slate-400'} h-2 rounded-full`} style={{ width: `${pct}%` }} /></div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            // --- Vote form ---
            <div className="space-y-4 py-2">
              <div data-testid="survey-warning-banner" className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                ⚠️ Your response <strong>cannot be changed</strong> after submission. Closes <span className="font-semibold">{formatExpiry(active.expires_at)}</span>.
              </div>
              {active.questions.map((q, qIdx) => (
                <div key={qIdx} className="space-y-2" data-testid={`survey-resp-q-${qIdx}`}>
                  <p className="text-sm font-semibold">{qIdx + 1}. {q.question}</p>
                  <div className="space-y-1">
                    {q.options.map((o, oIdx) => (
                      <button key={oIdx} data-testid={`survey-resp-q-${qIdx}-opt-${oIdx}`} type="button" onClick={() => setAnswers({ ...answers, [qIdx]: oIdx })} className={`w-full text-left p-2 rounded-lg border-2 text-sm ${answers[qIdx] === oIdx ? 'border-indigo-500 bg-indigo-50 text-indigo-700 font-semibold' : 'border-slate-200 text-slate-700'}`}>{o}</button>
                    ))}
                  </div>
                </div>
              ))}
              <Button data-testid="survey-resp-submit" onClick={submit} disabled={submitting} className="w-full bg-indigo-600 hover:bg-indigo-700">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Submit Response
              </Button>
            </div>
          )
        ) : surveys.length === 0 ? <p className="text-center text-sm text-slate-500 py-6">No active surveys right now.</p> : (
          <div className="space-y-2 py-2">
            {surveys.map((s) => (
              <button key={s.id} data-testid={`survey-pick-${s.id}`} onClick={() => { setActive(s); setAnswers({}); }} className={`w-full text-left p-3 rounded-lg border-2 ${s.responded ? 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100' : 'bg-indigo-50 border-indigo-200 hover:bg-indigo-100'}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-sm truncate">{s.title}</p>
                    <p className="text-xs text-slate-500">{s.questions.length} question(s) · by {s.created_by_name} · closes {formatExpiry(s.expires_at)}</p>
                  </div>
                  {s.responded ? <Badge className="bg-emerald-500 text-[10px]">View results</Badge> : <Badge className="bg-indigo-500 text-[10px]">Open</Badge>}
                </div>
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// --- Chat Modal ---
export function ChatModal({ open, onOpenChange, currentUserId }) {
  const [view, setView] = useState('threads'); // 'threads' | 'room' | 'new'
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeThread, setActiveThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const fileRef = useRef(null);

  const loadThreads = useCallback(async () => {
    setLoading(true);
    try { const { data } = await chatAPI.threads(); setThreads(data || []); }
    catch { /* silent */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (open && view === 'threads') loadThreads(); }, [open, view, loadThreads, refreshKey]);

  // Polling for messages while in a room
  const loadMessages = useCallback(async (threadId) => {
    try { const { data } = await chatAPI.messages(threadId); setMessages(data || []); }
    catch { /* silent */ }
  }, []);
  useEffect(() => {
    if (view !== 'room' || !activeThread) return;
    loadMessages(activeThread.id);
    chatAPI.markRead(activeThread.id).catch(() => {});
    const id = setInterval(() => loadMessages(activeThread.id), 4000);
    return () => clearInterval(id);
  }, [view, activeThread, loadMessages]);

  const send = async (imagePath) => {
    if (!text.trim() && !imagePath) return;
    setSending(true);
    try {
      await chatAPI.send(activeThread.id, text.trim() || null, imagePath || null);
      setText('');
      loadMessages(activeThread.id);
    } catch { toast.error('Failed to send'); }
    finally { setSending(false); }
  };

  const onFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image too large (max 5MB)'); return; }
    const fd = new FormData(); fd.append('file', file);
    try {
      const { data } = await chatAPI.uploadImage(activeThread.id, fd);
      await send(data.path);
    } catch { toast.error('Upload failed'); }
    e.target.value = '';
  };

  const handleBlock = async () => {
    if (!activeThread || activeThread.type !== 'direct') return;
    const otherId = activeThread.member_ids.find(m => m !== currentUserId);
    if (!otherId) return;
    if (!window.confirm(`Block ${activeThread.display_name}?\n\nThey won't be able to message you. This thread will be hidden from your view. They will not be notified.`)) return;
    try {
      await chatAPI.blockUser(otherId);
      toast.success(`Blocked ${activeThread.display_name}`);
      setView('threads');
      setActiveThread(null);
      setRefreshKey(k => k + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to block');
    }
  };

  const handleReport = async (msg) => {
    const reason = window.prompt('Reason for reporting? (e.g., spam, harassment)');
    if (!reason || !reason.trim()) return;
    try { await chatAPI.reportMessage(msg.id, reason, ''); toast.success('Reported to management'); }
    catch { toast.error('Failed to report'); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setView('threads'); setActiveThread(null); setMessages([]); } onOpenChange(v); }}>
      <DialogContent data-testid="chat-modal" className="max-w-md h-[90vh] sm:h-[85vh] p-0 flex flex-col overflow-hidden">
        <DialogHeader className="p-3 sm:p-4 border-b shrink-0 bg-white">
          {view === 'threads' && (
            <DialogTitle className="flex items-center justify-between pr-8 m-0">
              <span className="text-base font-semibold">Neighbors Chat</span>
              <Button data-testid="chat-new-thread-btn" size="sm" onClick={() => setView('new')} className="bg-cyan-600 hover:bg-cyan-700 h-8 px-2.5"><Plus className="w-4 h-4" /></Button>
            </DialogTitle>
          )}
          {view === 'room' && activeThread && (
            <DialogTitle className="flex items-center gap-2.5 pr-8 m-0">
              <button data-testid="chat-back-btn" onClick={() => { setView('threads'); setActiveThread(null); setRefreshKey(k => k + 1); }} className="flex items-center gap-2 min-w-0 flex-1 active:opacity-60 transition-opacity">
                <span className="relative shrink-0">
                  <span className={`flex items-center justify-center w-9 h-9 rounded-full text-white text-sm font-semibold ${activeThread.type === 'group' ? 'bg-pink-500' : 'bg-cyan-500'}`}>
                    {activeThread.type === 'group' ? <Users className="w-4 h-4" /> : (activeThread.display_name || '?')[0]?.toUpperCase()}
                  </span>
                  <span className="absolute -left-1 -top-1 w-4 h-4 rounded-full bg-slate-100 flex items-center justify-center"><ArrowLeft className="w-2.5 h-2.5 text-slate-500" /></span>
                </span>
                <span className="min-w-0 flex-1 text-left">
                  <span className="block text-sm font-semibold truncate">{activeThread.display_name || activeThread.name}</span>
                  <span className="block text-[11px] font-normal text-slate-500 truncate">{activeThread.type === 'group' ? `Group${activeThread.member_count ? ` · ${activeThread.member_count} members` : ''}` : 'Direct chat'}</span>
                </span>
              </button>
              {activeThread.type === 'direct' && <button data-testid="chat-block-btn" onClick={handleBlock} title="Block this neighbor" className="text-slate-400 hover:text-red-500 p-1.5 rounded-full hover:bg-red-50"><ShieldOff className="w-4 h-4" /></button>}
            </DialogTitle>
          )}
          {view === 'new' && (
            <DialogTitle className="flex items-center gap-2 pr-8 m-0">
              <button onClick={() => setView('threads')} className="text-slate-500 active:text-slate-700 p-1.5 -ml-1.5"><ArrowLeft className="w-4 h-4" /></button>
              <span className="text-base font-semibold">New conversation</span>
            </DialogTitle>
          )}
          <DialogDescription className="sr-only">Chat with neighbors</DialogDescription>
        </DialogHeader>

        {view === 'threads' && (
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : threads.length === 0 ? <p className="text-center text-sm text-slate-500 py-6">No conversations yet — tap + to start one.</p> : threads.map((t) => (
              <button key={t.id} data-testid={`chat-thread-${t.id}`} onClick={() => { setActiveThread(t); setView('room'); }} className="w-full text-left p-3 bg-slate-50 hover:bg-slate-100 rounded-lg border border-slate-200 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${t.type === 'group' ? 'bg-pink-500' : 'bg-cyan-500'}`}>{t.type === 'group' ? <Users className="w-5 h-5" /> : (t.display_name || '?')[0]?.toUpperCase()}</div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate">{t.display_name || t.name}</p>
                  <p className="text-xs text-slate-500 truncate">{t.last_message_preview || 'No messages yet'}</p>
                </div>
                {t.unread_count > 0 && <Badge className="bg-red-500 text-[10px]">{t.unread_count}</Badge>}
              </button>
            ))}
          </div>
        )}

        {view === 'room' && activeThread && (
          <>
            <div data-testid="chat-transient-banner" className="px-3 py-1.5 bg-slate-100 border-b border-slate-200 flex items-center justify-center gap-1.5 text-[11px] text-slate-500 shrink-0">
              <Clock className="w-3 h-3" />
              <span>Messages disappear after 24 hours</span>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50">
              {messages.length === 0 ? <p className="text-center text-sm text-slate-400 py-4">No messages yet.</p> : messages.map((m) => {
                const mine = m.sender_id === currentUserId;
                return (
                  <div key={m.id} data-testid={`chat-msg-${m.id}`} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] p-2 rounded-lg ${mine ? 'bg-cyan-500 text-white' : 'bg-white text-slate-900 border border-slate-200'}`}>
                      {!mine && <p className="text-[10px] font-semibold opacity-80">{m.sender_name}</p>}
                      {m.body && <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>}
                      {m.image_path && <img src={chatAPI.imageUrl(m.id)} alt="attachment" className="mt-1 max-w-full rounded" />}
                      <div className={`text-[10px] mt-0.5 ${mine ? 'text-cyan-100' : 'text-slate-400'} flex items-center gap-1`}>
                        <span>{new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        {!mine && <button data-testid={`chat-report-${m.id}`} onClick={() => handleReport(m)} className="ml-1 opacity-60 hover:opacity-100"><Flag className="w-3 h-3" /></button>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="p-2 border-t shrink-0 flex gap-1 bg-white">
              <input ref={fileRef} type="file" accept="image/*" onChange={onFileSelected} className="hidden" data-testid="chat-image-input" />
              <Button data-testid="chat-attach-btn" size="sm" variant="ghost" onClick={() => fileRef.current?.click()} className="text-slate-500"><ImageIcon className="w-5 h-5" /></Button>
              <Input data-testid="chat-message-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), send())} placeholder="Message..." className="flex-1" />
              <Button data-testid="chat-send-btn" size="sm" onClick={() => send()} disabled={sending || !text.trim()} className="bg-cyan-600 hover:bg-cyan-700">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</Button>
            </div>
          </>
        )}

        {view === 'new' && <NewThreadView onCreated={(t) => { setActiveThread(t); setView('room'); setRefreshKey(k => k + 1); }} />}
      </DialogContent>
    </Dialog>
  );
}

function NewThreadView({ onCreated }) {
  const [residents, setResidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [type, setType] = useState('direct');
  const [selected, setSelected] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    chatAPI.messageableResidents().then(r => setResidents(r.data || [])).catch(() => toast.error('Failed to load residents')).finally(() => setLoading(false));
  }, []);

  const toggle = (id) => {
    if (type === 'direct') setSelected([id]);
    else setSelected(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]);
  };

  const create = async () => {
    if (!selected.length) return toast.error('Pick at least one resident');
    if (type === 'group' && !groupName.trim()) return toast.error('Enter a group name');
    setSubmitting(true);
    try {
      const { data } = await chatAPI.createThread(type, selected, type === 'group' ? groupName.trim() : null);
      onCreated && onCreated(data.thread);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
    finally { setSubmitting(false); }
  };

  const q = search.trim().toLowerCase();
  const filtered = q ? residents.filter(r => (r.name || '').toLowerCase().includes(q) || (r.villa_number || '').toLowerCase().includes(q)) : residents;

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="p-3 space-y-2 shrink-0">
        <div className="grid grid-cols-2 gap-2">
          <button data-testid="new-thread-type-direct" type="button" onClick={() => { setType('direct'); setSelected([]); }} className={`p-2 rounded-lg border-2 text-xs ${type === 'direct' ? 'border-cyan-500 bg-cyan-50 text-cyan-700 font-semibold' : 'border-slate-200 text-slate-600'}`}>Direct (1-on-1)</button>
          <button data-testid="new-thread-type-group" type="button" onClick={() => { setType('group'); setSelected([]); }} className={`p-2 rounded-lg border-2 text-xs ${type === 'group' ? 'border-pink-500 bg-pink-50 text-pink-700 font-semibold' : 'border-slate-200 text-slate-600'}`}>Group (block/tower)</button>
        </div>
        {type === 'group' && <Input data-testid="new-thread-group-name" value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder="Group name (e.g., Tower A)" />}
        <div className="relative"><Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" /><Input data-testid="new-thread-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search neighbors..." className="pl-8" /></div>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-1">
        {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : filtered.length === 0 ? <p className="text-center text-xs text-slate-500 py-4">No neighbors found. Have they enabled the directory and chat preference?</p> : filtered.map((r) => {
          const sel = selected.includes(r.id);
          return (
            <button key={r.id} data-testid={`new-thread-pick-${r.id}`} onClick={() => toggle(r.id)} className={`w-full text-left p-2 rounded-lg border ${sel ? 'border-cyan-500 bg-cyan-50' : 'border-slate-200 bg-white'}`}>
              <div className="flex items-center justify-between">
                <div><p className="text-sm font-semibold">{r.name}</p><p className="text-xs text-slate-500">Villa {r.villa_number}</p></div>
                {sel && <Badge className="bg-cyan-500">✓</Badge>}
              </div>
            </button>
          );
        })}
      </div>
      <div className="p-3 border-t shrink-0">
        <Button data-testid="new-thread-create-btn" onClick={create} disabled={submitting || !selected.length} className="w-full bg-cyan-600 hover:bg-cyan-700">
          {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Start conversation ({selected.length})
        </Button>
      </div>
    </div>
  );
}

// --- Profile chat preference card ---
export function ChatPreferenceCard({ allowMessages, onChange }) {
  const [blocks, setBlocks] = useState([]);
  const [showBlocks, setShowBlocks] = useState(false);

  const loadBlocks = useCallback(async () => {
    try { const { data } = await chatAPI.listBlocks(); setBlocks(data || []); } catch { /* silent */ }
  }, []);
  useEffect(() => { if (showBlocks) loadBlocks(); }, [showBlocks, loadBlocks]);

  const unblock = async (userId, name) => {
    if (!window.confirm(`Unblock ${name}? They'll be able to message you again.`)) return;
    try { await chatAPI.unblockUser(userId); toast.success('Unblocked'); loadBlocks(); }
    catch { toast.error('Failed'); }
  };

  return (
    <Card className="shadow-lg border-0" data-testid="chat-pref-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-slate-800">Allow direct messages</p>
            <p className="text-xs text-slate-500 mt-0.5">When off, neighbors can't message you. (Default: on)</p>
          </div>
          <button data-testid="chat-pref-toggle" type="button" onClick={() => onChange(!allowMessages)} className={`relative inline-flex h-6 w-11 items-center rounded-full ${allowMessages ? 'bg-gk-teal' : 'bg-slate-300'}`}>
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${allowMessages ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>
        <button data-testid="chat-blocks-toggle" type="button" onClick={() => setShowBlocks(!showBlocks)} className="text-xs text-slate-500 hover:text-slate-700 underline">
          {showBlocks ? 'Hide blocked neighbors' : 'View blocked neighbors'} {blocks.length > 0 && showBlocks ? `(${blocks.length})` : ''}
        </button>
        {showBlocks && (
          <div className="space-y-1">
            {blocks.length === 0 ? <p className="text-xs text-slate-400">No blocked neighbors.</p> : blocks.map((b) => (
              <div key={b.id} data-testid={`blocked-row-${b.blocked_id}`} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                <div className="text-xs"><span className="font-semibold">{b.name}</span><span className="text-slate-400 ml-1">Villa {b.villa_number}</span></div>
                <Button data-testid={`unblock-btn-${b.blocked_id}`} size="sm" variant="ghost" onClick={() => unblock(b.blocked_id, b.name)} className="text-emerald-600 h-7 text-xs">Unblock</Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
